import { useTranslation } from 'react-i18next';
import { formatXPF, formatCurrency, formatDateTime } from '@/utils/format';
import { RequestStatusBadge } from './StatusBadge';
import type { AdminRequest } from '@/mocks/adminRequests';

type OverviewTabProps = {
  stats: { totalClients: number; totalBalance: number; pending: number; processing: number };
  requests: AdminRequest[];
  onViewRequests: () => void;
};

export default function OverviewTab({ stats, requests, onViewRequests }: OverviewTabProps) {
  const { t } = useTranslation();

  const statCards = [
    {
      key: 'clients',
      icon: 'ri-user-3-line',
      label: t('admin.overview.clients'),
      value: String(stats.totalClients),
      accent: 'bg-primary-100 text-primary-800',
    },
    {
      key: 'balance',
      icon: 'ri-bank-line',
      label: t('admin.overview.balance'),
      value: formatXPF(stats.totalBalance),
      accent: 'bg-accent-100 text-accent-900',
    },
    {
      key: 'pending',
      icon: 'ri-time-line',
      label: t('admin.overview.pending'),
      value: String(stats.pending),
      accent: 'bg-secondary-100 text-secondary-900',
    },
    {
      key: 'processing',
      icon: 'ri-loader-4-line',
      label: t('admin.overview.processing'),
      value: String(stats.processing),
      accent: 'bg-primary-100 text-primary-800',
    },
  ];

  const recent = requests.slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.overview.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.overview.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="rounded-lg border border-background-200 bg-background-50 p-5"
          >
            <span className={`flex h-10 w-10 items-center justify-center rounded-md ${card.accent}`}>
              <i className={`${card.icon} text-lg`}></i>
            </span>
            <p className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
              {card.value}
            </p>
            <p className="mt-1 text-sm text-foreground-600">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
          <h2 className="font-heading text-base font-semibold text-foreground-950">
            {t('admin.overview.recentTitle')}
          </h2>
          <button
            type="button"
            onClick={onViewRequests}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
          >
            {t('admin.overview.viewAll')}
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.requests.reference')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.client')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.amount')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.requests.date')}</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((request) => (
                <tr
                  key={request.id}
                  className="border-b border-background-100 last:border-0 hover:bg-background-50"
                >
                  <td className="px-5 py-3 font-semibold text-foreground-800">{request.reference}</td>
                  <td className="px-5 py-3 text-foreground-700">{request.clientName}</td>
                  <td className="px-5 py-3 font-semibold text-foreground-900">
                    {formatCurrency(request.amount, request.currency)}
                  </td>
                  <td className="px-5 py-3">
                    <RequestStatusBadge status={request.status} />
                  </td>
                  <td className="px-5 py-3 text-foreground-600">{formatDateTime(request.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}